/**
 * Helper funcitons to manage "open groups", groups that don't have closing symbol.
 * These groups should absorb or release nodes when you move cursor.
 * These functions have pairs: the function to test if we can do the action, and the function that does that action.
 * Example of usage of these functions:
 * ```
 * if (shouldReopenGroup(doc)) {
 *   reopenGroup(doc)
 * }
 * ```
 */

import { hasGroupBounds } from "@/components/expression/utils/matrix";
import { cursorIsActive } from "@/models/CursorModel";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { isGroupClosingSymbol } from "@/models/nodes/groups/utils";
import { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { ClosingSymbol, OpeningSymbol, ParenthesesVisibility } from "@corca/data";

export function getGroup(node?: NodeModel): GroupNodeModel | undefined {
  const matrix = node?.parent?.is(MatrixNodeModel) ? node.parent : undefined;

  if (matrix?.parent?.is(GroupNodeModel)) {
    return matrix.parent;
  }

  return undefined;
}

/**
 * Check it the cursor is right before the unfinished group node. We want to move group boundaries with the
 * cursor in this case.
 */
function isOpenGroupLeft(doc: DocumentModel): GroupNodeModel | undefined {
  const { node, index } = doc.cursor;
  const group = getGroup(node);

  if (group?.hasWrappingDecorators) {
    return undefined;
  }

  if (group && group.openingSymbol === undefined && index === 0) {
    return group;
  }

  return undefined;
}

function isOpenGroupAfterCursor(
  doc: DocumentModel,
  maxDistance: number = Infinity,
): GroupNodeModel | undefined {
  let n = 0;

  for (const node of doc.cursor.nodesAfterCursor) {
    n += 1;
    if (node?.is(GroupNodeModel)) {
      if (node.hasWrappingDecorators) {
        return undefined;
      }

      return node.openingSymbol === undefined ? node : undefined;
    }
    if (n >= maxDistance) return undefined;
  }

  return undefined;
}

/**
 * Check it the cursor is right after the unfinished group node. We want to move group boundaries with the
 * cursor in this case.
 */
function isOpenGroupRight(doc: DocumentModel): GroupNodeModel | undefined {
  const group = getGroupWithCursorAtEnd(doc);
  return group?.closingSymbol === undefined ? group : undefined;
}

function getGroupWithCursorAtEnd(doc: DocumentModel): GroupNodeModel | undefined {
  const { node, index } = doc.cursor;
  const group = getGroup(node);

  if (group && index === node?.nodes.length) {
    if (group.casesSystemEnabled || group.hasWrappingDecorators) {
      return undefined;
    }

    if (group.isExpandable) {
      return group;
    }

    const position = group.matrixNode?.getCellPosition(node);

    if (position && position.col === (group.matrixNode?.cols || 0) - 1) {
      return group;
    }

    return undefined;
  }

  return undefined;
}

/**
 * Group whose closing symbol can be replaced with the typed one: the cursor is at the end of a
 * group that already has a different closing symbol, so typing a non-matching closing symbol
 * should offer to re-close it.
 */
export function getReplaceableClosingGroup(
  doc: DocumentModel,
  symbol: string,
): GroupNodeModel | null {
  if (!isGroupClosingSymbol(symbol)) {
    return null;
  }

  const group = getGroupWithCursorAtEnd(doc);

  if (group?.closingSymbol === undefined || group.closingSymbol === symbol) {
    return null;
  }

  return group;
}

function isOpenGroupBeforeCursor(
  doc: DocumentModel,
  maxDistance: number = Infinity,
): GroupNodeModel | undefined {
  let n = 0;

  for (const node of doc.cursor.nodesBeforeCursor) {
    n += 1;
    if (node?.is(GroupNodeModel)) {
      if (node.casesSystemEnabled || node.hasWrappingDecorators) {
        return undefined;
      }

      return node.closingSymbol === undefined ? node : undefined;
    }
    if (n >= maxDistance) return undefined;
  }

  return undefined;
}

export function shouldOpenGroupDecreaseLeft(doc: DocumentModel): boolean {
  const { node, index } = doc.cursor;
  if (node?.nodes.length === index) {
    return false;
  }
  return !!isOpenGroupLeft(doc)?.isExpandable;
}

/**
 * Handle cursor move left for open group. It should move the last node out of the group: `(|x+y)` => `x(|+y)`
 */
export function openGroupDecreaseLeft(doc: DocumentModel) {
  if (shouldOpenGroupDecreaseLeft(doc)) {
    const group = isOpenGroupLeft(doc);
    const nodeToMove = group?.inlineNode?.nodes[0];
    if (group && nodeToMove) {
      group.insertSiblingBefore(nodeToMove);
      doc.cursor.moveTo(group.inlineNode, 0);
    }
  }
}

export function shouldOpenGroupDecreaseRight(doc: DocumentModel): boolean {
  const { index } = doc.cursor;
  if (index === 0) {
    return false;
  }
  const group = isOpenGroupRight(doc);

  return !!(group && group.isExpandable);
}

/**
 * Handle cursor move left for open group. It should move the last node out of the group: `(x+y|)` => `(x+|)y`
 */
export function openGroupDecreaseRight(doc: DocumentModel) {
  if (shouldOpenGroupDecreaseRight(doc)) {
    const group = isOpenGroupRight(doc);
    const nodeToMove =
      group?.inlineNode?.nodes && group.inlineNode.nodes[group.inlineNode.nodes.length - 1];
    if (group && nodeToMove) {
      group.insertSiblingAfter(nodeToMove);
      doc.cursor.moveTo(group.inlineNode, group.inlineNode.nodes.length);
    }
  }
}

export function shouldOpenGroupIncreaseLeft(doc: DocumentModel): boolean {
  const moveGroup = isOpenGroupLeft(doc);

  if (!moveGroup?.isExpandable || doc.input.editingNode) {
    return false;
  }

  // Check there's a previous sibling to absorb
  const prevNode = moveGroup.prevSiblings[0];
  if (!prevNode) {
    return false;
  }

  return true;
}

/**
 * Handle cursor move right for open group. It should move the last node inside the group: `x(|+y)` => `(|x+y)`
 */
export function openGroupIncreaseLeft(doc: DocumentModel) {
  const group = isOpenGroupLeft(doc);
  const prevNode = group?.prevSiblings[0];
  if (group && prevNode && shouldOpenGroupIncreaseLeft(doc) && group.inlineNode) {
    const firstGroupNode = group.inlineNode.nodes[0];
    if (firstGroupNode) {
      firstGroupNode.insertSiblingBefore(prevNode);
    } else {
      group.inlineNode.appendChild(prevNode);
    }
    doc.cursor.moveTo(group.inlineNode, 0);
  }
}

export function shouldOpenGroupIncreaseRight(doc: DocumentModel): boolean {
  const moveGroup = isOpenGroupRight(doc);
  const nextNode = moveGroup?.nextSiblings[0];

  if (moveGroup && moveGroup.isExpandable && nextNode) {
    return true;
  }
  return false;
}

/**
 * Handle cursor move right for open group. It should move the last node inside the group: `(x+|)y` => `(x+y|)`
 */
export function openGroupIncreaseRight(doc: DocumentModel) {
  const group = isOpenGroupRight(doc);
  const nextNode = group?.nextSiblings[0];
  if (group && nextNode && group.inlineNode) {
    group.inlineNode.appendChild(nextNode);
    doc.cursor.moveTo(group.inlineNode, group.inlineNode.nodes.length);
  }
}

export function shouldCloseOpenGroupLeft(doc: DocumentModel): GroupNodeModel | null {
  return (isOpenGroupLeft(doc) || isOpenGroupAfterCursor(doc)) ?? null;
}

export function shouldApplyGroupSymbols(doc: DocumentModel): FunctionNodeModel | null {
  const { node, index } = doc.cursor;

  if (!node?.parent?.is(FunctionNodeModel)) {
    return null;
  }

  if (index === 0 || (index === node.nodes.length && node.parent.paramsAreEmpty)) {
    return node.parent.omitParentheses ? node.parent : null;
  }

  return null;
}

export function applyGroupSymbols(
  node: FunctionNodeModel,
  openingSymbol?: OpeningSymbol,
  closingSymbol?: ClosingSymbol,
) {
  node.openingSymbol = openingSymbol;
  node.closingSymbol = closingSymbol;
  node.parenthesesVisibility = "visible";
}

/** The function node a brackets action should target when one is selected. */
export function selectedFunctionNode(doc: DocumentModel): FunctionNodeModel | null {
  const { selectedNodes } = doc.selection;
  const [node] = selectedNodes;

  if (selectedNodes.length !== 1 || !node?.is(FunctionNodeModel)) {
    return null;
  }

  return node;
}

export function setFunctionParenthesesVisibility(
  node: FunctionNodeModel,
  parenthesesVisibility: ParenthesesVisibility,
) {
  node.parenthesesVisibility = parenthesesVisibility;

  if (parenthesesVisibility === "hidden") {
    node.openingSymbol = undefined;
    node.closingSymbol = undefined;
  } else if (parenthesesVisibility === "visible") {
    node.openingSymbol = "(";
    node.closingSymbol = ")";
  }
}

/**
 * Close opened group, add closing symbol.
 */
export function closeOpenGroupLeft(doc: DocumentModel, openingSymbol: OpeningSymbol) {
  let openGroupNode = isOpenGroupAfterCursor(doc, 1) || isOpenGroupLeft(doc);
  if (openGroupNode) {
    openGroupNode.openingSymbol = openingSymbol;
    if (openGroupNode.matrixNode) {
      doc.cursor.moveTo(openGroupNode.matrixNode.matrixNodes[0], 0);
    }
    return true;
  }

  openGroupNode = isOpenGroupAfterCursor(doc);

  if (openGroupNode?.matrixNode?.rows === 1) {
    const nodesToMove: NodeModel[] = [];
    for (const node of doc.cursor.nodesAfterCursor) {
      if (node === openGroupNode) break;
      nodesToMove.push(node);
    }
    openGroupNode.matrixNode.matrixNodes[0]?.prependChildren(nodesToMove);
    openGroupNode.openingSymbol = openingSymbol;
    doc.cursor.moveTo(openGroupNode.matrixNode.matrixNodes[0], 0);
    return true;
  }

  return false;
}

export function shouldCloseOpenGroupRight(doc: DocumentModel): GroupNodeModel | null {
  return (isOpenGroupRight(doc) || isOpenGroupBeforeCursor(doc)) ?? null;
}

/**
 * Close opened group, add closing symbol.
 */
export function closeOpenGroupRight(doc: DocumentModel, closingSymbol: ClosingSymbol) {
  let openGroupNode: GroupNodeModel | null | undefined =
    isOpenGroupBeforeCursor(doc, 1) ||
    isOpenGroupRight(doc) ||
    getReplaceableClosingGroup(doc, closingSymbol);

  if (openGroupNode) {
    openGroupNode.closingSymbol = closingSymbol;
    doc.cursor.moveTo(openGroupNode.parent, openGroupNode.index + 1);
    return true;
  }

  openGroupNode = isOpenGroupBeforeCursor(doc);

  if (openGroupNode?.matrixNode?.rows === 1) {
    const nodesToMove: NodeModel[] = [];
    for (const node of doc.cursor.nodesBeforeCursor) {
      if (node === openGroupNode) break;
      nodesToMove.unshift(node);
    }
    openGroupNode.matrixNode.nodes.at(-1)?.appendChildren(nodesToMove);
    openGroupNode.closingSymbol = closingSymbol;
    doc.cursor.moveTo(openGroupNode.parent, openGroupNode.index + 1);
    return true;
  }

  return false;
}

export function shouldReopenGroupRight(doc: DocumentModel) {
  const { nodeBeforeCursor } = doc.cursor;
  return (
    nodeBeforeCursor?.is(GroupNodeModel) &&
    nodeBeforeCursor.closingSymbol !== undefined &&
    nodeBeforeCursor.openingSymbol !== undefined
  );
}

/**
 * Reopen a group, remove the closing symbol
 */
export function reopenGroupRight(doc: DocumentModel) {
  const { nodeBeforeCursor } = doc.cursor;
  if (shouldReopenGroupRight(doc) && nodeBeforeCursor?.is(GroupNodeModel)) {
    nodeBeforeCursor.closingSymbol = undefined;

    if (nodeBeforeCursor.inlineNode) {
      doc.cursor.moveTo(nodeBeforeCursor.inlineNode, nodeBeforeCursor.inlineNode.nodes.length);
    }
  }
}

export function shouldReopenGroupLeft(doc: DocumentModel) {
  if (!cursorIsActive(doc.cursor)) {
    return false;
  }

  const { node, index } = doc.cursor;
  const group = getGroup(node);

  const hasSymbols = group?.openingSymbol !== undefined && group?.closingSymbol !== undefined;

  if (group?.isExpandable) {
    return index === 0 && hasSymbols;
  }

  const cell = group?.matrixNode?.getCellNodeByPosition({ row: 0, col: 0 });
  return index === 0 && hasSymbols && cell === node;
}

/**
 * Reopen a group, remove the closing symbol
 */
export function reopenGroupLeft(doc: DocumentModel) {
  const group = getGroup(doc.cursor.node);

  if (shouldReopenGroupLeft(doc) && group) {
    group.openingSymbol = undefined;
  }
}

function isUngroupable(node?: NodeModel): node is GroupNodeModel {
  if (!node?.is(GroupNodeModel)) {
    return false;
  }

  if (node.matrixNode && !hasGroupBounds(node.matrixNode.bounds)) {
    return false;
  }

  return true;
}

function isBracketlessGroup(node: NodeModel | undefined) {
  return (
    isUngroupable(node) &&
    node.openingSymbol === undefined &&
    node.closingSymbol === undefined &&
    node.decorators.length === 0
  );
}

function isOpenGroup(node: NodeModel | undefined, side: "left" | "right") {
  if (!isUngroupable(node)) {
    return false;
  }

  if (side === "right") {
    return node.closingSymbol === undefined && node.openingSymbol !== undefined;
  }

  return node.closingSymbol !== undefined && node.openingSymbol === undefined;
}

export function shouldUngroup(doc: DocumentModel, direction: "left" | "right" = "left") {
  const { nodeBeforeCursor, nodeAfterCursor } = doc.cursor;
  const cursorNode = direction === "right" ? nodeAfterCursor : nodeBeforeCursor;

  if (cursorNode) {
    return isOpenGroup(cursorNode, direction);
  }

  const group = getGroup(doc.cursor.node);

  if (direction === "right" && doc.cursor.index === doc.cursor.node?.nodes.length) {
    return isOpenGroup(group, "left");
  }

  if (direction === "left" && doc.cursor.index === 0) {
    return isOpenGroup(group, "right") || isBracketlessGroup(group);
  }

  return false;
}

export function shouldMoveToOpenGroupLeft(doc: DocumentModel) {
  const { nodeBeforeCursor } = doc.cursor;
  return nodeBeforeCursor?.is(GroupNodeModel) && nodeBeforeCursor.closingSymbol === undefined;
}

export function shouldMoveToOpenGroupRight(doc: DocumentModel) {
  const { nodeAfterCursor } = doc.cursor;
  return nodeAfterCursor?.is(GroupNodeModel) && nodeAfterCursor.openingSymbol === undefined;
}

export function shouldMoveOutOfOpenGroupLeft(doc: DocumentModel) {
  const { node, index } = doc.cursor;
  const group = getGroup(node);

  if (group) {
    return index === node?.nodes.length && group?.closingSymbol === undefined;
  }

  return false;
}

export function shouldMoveOutOfOpenGroupRight(doc: DocumentModel) {
  const { node, index } = doc.cursor;
  const group = getGroup(node);

  if (group) {
    return index === 0 && group?.openingSymbol === undefined;
  }

  return false;
}

export function ungroup(doc: DocumentModel, direction: "left" | "right" = "left"): void {
  const { node, nodeBeforeCursor, nodeAfterCursor, index: cursorIndex = -1 } = doc.cursor;

  if (!cursorIsActive(doc.cursor) || cursorIndex === -1) {
    return;
  }

  const adjacentGroup = direction === "right" ? nodeAfterCursor : nodeBeforeCursor;
  const group = adjacentGroup ?? getGroup(node);
  const cursorIsAdjacent = !!adjacentGroup;

  if (!isUngroupable(group)) {
    return;
  }

  if (group.inlineNode) {
    const children = group.inlineNode.nodes;
    let index: number;

    if (cursorIsAdjacent) {
      index = cursorIndex <= group.index ? cursorIndex : cursorIndex + children.length - 1;
    } else {
      index = group.index + Math.min(children.length, cursorIndex);
    }

    group.inlineNode.nodes.forEach((n) => group.insertSiblingBefore(n));
    doc.cursor.moveTo(group.parent, index);
    doc.removeNode(group);
  }
}
